import { useEffect, useState } from "react";
import SideMenu from "../../Layout/CustomLayout/SideMenu";
import CustomLayout from "../../Layout/CustomLayout";
import {
  Col,
  DatePicker,
  Image,
  Row,
  Segmented,
  Select,
  Typography,
} from "antd";
import typoStyles from "../../../styles/Custom/Typography.module.css";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import calendarImg from "../../../assets/images/nurseIcons/calender.svg";
import inputStyles from "../../../styles/Custom/Input.module.css";
import AdmissionsOverview from "./AdmissionsOverview.js/AdmissionsOverview";
import BedOccupancy from "./BedOccupancy/BedOccupancy";
import FinancialMetrics from "./FinancialMetrics/FinancialMetrics";
import ARPOBAnalysis from "./ARPOBAnalysis/ARPOBAnalysis";
import { getFacilityListApi } from "../../../services/facilities.services";
import { getCookie } from "../../../helpers/localStorage";
import { FacilityID, Role } from "../../../constants/defaultKeys";
import useQuery from "../../../hooks/useQuery";
import { updateUrlSearchParams } from "../../../helpers/urlFilters";

const { RangePicker } = DatePicker;
const dateFormat = "YYYY-MM-DD";

const segmentOptions = [
  { label: "Admissions Overview", value: "admissions-overview" },
  { label: "Bed Occupancy and Efficiency", value: "bed-occupancy" },
  { label: "Financial Metrics", value: "financial-metrics" },
  { label: "ARPOB Analysis", value: "arpob-analysis" },
];

const disabledDate = (current) => {
  return current && current > dayjs().endOf("day");
};

dayjs.extend(isoWeek);

const OrgDashboard = () => {
  const startOfWeek = dayjs().startOf("isoWeek");
  const endOfWeek = dayjs().endOf("isoWeek");
  const facilityAdmin =
    getCookie(Role) === "Facility_Admin" ? getCookie(FacilityID) : null;
  const navigate = useNavigate();
  const params = useQuery();
  const activeSegment = params?.type || "admissions-overview";
  const chartPeriod = params?.chart_period || "week";
  const currentFacility = facilityAdmin
    ? Number(facilityAdmin)
    : params?.facility
      ? Number(params.facility)
      : null;
  const date = {
    start_date: params?.start_date || startOfWeek.format(dateFormat),
    end_date: params?.end_date || endOfWeek.format(dateFormat),
  };

  const handleSegmentChange = (val) => {
    updateUrlSearchParams(navigate, { type: val });
  };

  const onDateChange = (range) => {
    if (!range?.[0] || !range?.[1]) return;
    updateUrlSearchParams(navigate, {
      start_date: dayjs(range[0]).format(dateFormat),
      end_date: dayjs(range[1]).format(dateFormat),
    });
  };

  const onFacilityChange = (val) => {
    updateUrlSearchParams(navigate, { facility: val });
  };

  const onChartPeriodChange = (val) => {
    updateUrlSearchParams(navigate, { chart_period: val });
  };

  const [facilityData, setFacilityData] = useState([]);

  useEffect(() => {
    getFacilityListApi()
      .then((res) => {
        const data = res?.data;
        const facilityOptions = data?.map((option) => ({
          label: option?.name,
          value: option?.id,
        }));
        setFacilityData([{ label: "All", value: null }, ...facilityOptions]);
      })
      .catch((error) => console.log(`Error:${error}`));
  }, []);

  return (
    <>
      <CustomLayout
        header={
          <>
            <Typography className={typoStyles.h6}>
              Dashboard
              {/* {defaultRole === "Facility_Admin" ? "Facility" : "Facilities"} */}
            </Typography>
          </>
        }
        sideContent={<SideMenu />}
      >
        <Row gutter={[8, 32]}>
          <Col xs={24} sm={24} md={24} lg={14} xl={14} xxl={16}>
            <Segmented
              className="segment-wrap"
              style={{
                backgroundColor: "#F9FAFB",
              }}
              size="large"
              value={activeSegment}
              options={segmentOptions}
              onChange={handleSegmentChange}
            />
          </Col>
          <Col
            xs={24}
            sm={24}
            md={24}
            lg={10}
            xl={10}
            xxl={8}
            className="flex-end"
          >
            <Row gutter={[5, 5]}>
              {!facilityAdmin && (
                <Col
                  xs={24}
                  sm={24}
                  md={24}
                  lg={24}
                  xl={24}
                  xxl={8}
                  className="flex-end"
                >
                  <Select
                    value={currentFacility}
                    options={facilityData}
                    onSelect={onFacilityChange}
                    style={{ width: 150 }}
                    size="large"
                  />
                </Col>
              )}

              <Col
                xs={24}
                sm={24}
                md={24}
                lg={24}
                xl={24}
                xxl={facilityAdmin ? 24 : 16}
                className="flex-end"
              >
                <RangePicker
                  style={{
                    color: "black",
                    border: "1px solid #a182e3",
                    height: "38px",
                  }}
                  disabledDate={disabledDate}
                  className={inputStyles.mdDateInput}
                  onChange={onDateChange}
                  allowClear={false}
                  // value={[startOfWeek, endOfWeek]}
                  value={[
                    date.start_date ? dayjs(date.start_date) : null,
                    date.end_date ? dayjs(date.end_date) : null,
                  ]}
                  suffixIcon={
                    <Image width={20} src={calendarImg} preview={false} />
                  }
                />
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            {(() => {
              const chartProps = {
                currentFacility,
                date,
                chartPeriod,
                onChartPeriodChange,
              };
              switch (activeSegment) {
                case "admissions-overview":
                  return <AdmissionsOverview {...chartProps} />;
                case "bed-occupancy":
                  return <BedOccupancy {...chartProps} />;
                case "financial-metrics":
                  return <FinancialMetrics {...chartProps} />;
                case "arpob-analysis":
                  return <ARPOBAnalysis {...chartProps} />;
                default:
                  return <AdmissionsOverview {...chartProps} />;
              }
            })()}
          </Col>
        </Row>
      </CustomLayout>
    </>
  );
};

export default OrgDashboard;
